import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, BarChart3, ChefHat, ChevronRight, Clock, DollarSign, Eye, FileText, LayoutDashboard, LineChart, Menu, PieChart, Play, ShieldCheck, TrendingUp, Users, Utensils, Wallet, X, Zap, TrendingDown, AlertCircle, Layers, MousePointer2, Globe, CheckCircle2 } from 'lucide-react';
interface ROILandingPageProps {
  onBack?: () => void;
}
export function ROI({
  onBack
}: ROILandingPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-[#0A0A0B] text-white font-sans selection:bg-[#10B981] selection:text-white overflow-x-hidden" data-id="element-2168">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 border-b ${isScrolled ? 'bg-[#0A0A0B]/80 backdrop-blur-xl border-white/10 py-4' : 'bg-transparent border-transparent py-6'}`} data-id="element-2169">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-2170">
          <div className="flex items-center gap-2 group cursor-pointer" data-id="element-2171">
            <div className="w-10 h-10 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-xl flex items-center justify-center shadow-lg shadow-[#10B981]/20 group-hover:scale-110 transition-transform duration-500" data-id="element-2172">
              <TrendingUp size={24} className="text-white" data-id="element-2173" />
            </div>
            <span className="text-2xl font-black tracking-tighter" data-id="element-2174">ROI</span>
          </div>

          <div className="hidden lg:flex items-center gap-10 text-sm font-medium text-white/60" data-id="element-2175">
            <a href="#problem" className="hover:text-white transition-colors" data-id="element-2176">
              Problem
            </a>
            <a href="#how-it-works" className="hover:text-white transition-colors" data-id="element-2177">
              Solution
            </a>
            <a href="#benefits" className="hover:text-white transition-colors" data-id="element-2178">
              Benefits
            </a>
            <a href="#about" className="hover:text-white transition-colors" data-id="element-2179">
              About
            </a>
          </div>

          <div className="flex items-center gap-4" data-id="element-2180">
            {onBack && <button onClick={onBack} className="hidden md:block text-xs font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors mr-4" data-id="element-2181">
                ← Back to Admin
              </button>}
            <button className="hidden sm:block px-6 py-2.5 text-sm font-bold text-white/80 hover:text-white transition-colors" data-id="element-2182">
              Log In
            </button>
            <button className="px-6 py-2.5 bg-white text-black rounded-full text-sm font-bold hover:bg-[#10B981] hover:text-white transition-all duration-500 shadow-xl shadow-white/5" data-id="element-2183">
              Book a Demo
            </button>
            <button className="lg:hidden p-2 text-white/60 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(true)} data-id="element-2184">
              <Menu size={24} data-id="element-2185" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-2186">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-0 z-[200] bg-[#0A0A0B] p-8 flex flex-col" data-id="element-2187">
            <div className="flex justify-between items-center mb-12" data-id="element-2188">
              <div className="flex items-center gap-2" data-id="element-2189">
                <div className="w-10 h-10 bg-[#10B981] rounded-xl flex items-center justify-center" data-id="element-2190">
                  <TrendingUp size={24} data-id="element-2191" />
                </div>
                <span className="text-2xl font-black tracking-tighter" data-id="element-2192">
                  ROI
                </span>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-white/60" data-id="element-2193">
                <X size={32} data-id="element-2194" />
              </button>
            </div>
            <div className="flex flex-col gap-8 text-3xl font-bold" data-id="element-2195">
              <a href="#problem" onClick={() => setMobileMenuOpen(false)} data-id="element-2196">
                Problem
              </a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} data-id="element-2197">
                Solution
              </a>
              <a href="#benefits" onClick={() => setMobileMenuOpen(false)} data-id="element-2198">
                Benefits
              </a>
              <a href="#about" onClick={() => setMobileMenuOpen(false)} data-id="element-2199">
                About
              </a>
            </div>
            <div className="mt-auto pt-8 border-t border-white/10 flex flex-col gap-4" data-id="element-2200">
              <button className="w-full py-4 bg-white text-black rounded-2xl font-bold text-lg" data-id="element-2201">
                Book a Demo
              </button>
              <button className="w-full py-4 border border-white/10 rounded-2xl font-bold text-lg" data-id="element-2202">
                Log In
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 px-6 overflow-hidden" data-id="element-2203">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none" data-id="element-2204">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#10B981]/10 blur-[120px] rounded-full" data-id="element-2205"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-900/20 blur-[120px] rounded-full" data-id="element-2206"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-2207">
          <div className="text-center max-w-4xl mx-auto mb-20" data-id="element-2208">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-[#10B981] text-xs font-bold uppercase tracking-widest mb-8 backdrop-blur-md" data-id="element-2209">
              <span className="relative flex h-2 w-2" data-id="element-2210">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" data-id="element-2211"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" data-id="element-2212"></span>
              </span>
              Next-Gen Restaurant Intelligence
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-8" data-id="element-2213">
              Stop Guessing Where Your <br data-id="element-2214" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-white/40" data-id="element-2215">
                Restaurant Money Is Going
              </span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-xl md:text-2xl text-white/60 font-medium leading-relaxed mb-12 max-w-3xl mx-auto" data-id="element-2216">
              See your real food costs, labor costs, and spending — faster. ROI
              helps restaurant owners understand their money in, money out, and
              operational blind spots.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-2217">
              <button className="w-full sm:w-auto px-10 py-5 bg-white text-black rounded-full font-black text-lg hover:bg-[#10B981] hover:text-white transition-all duration-500 shadow-2xl shadow-white/10 group flex items-center justify-center gap-2" data-id="element-2218">
                Book a Demo
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-2219" />
              </button>
              <button className="w-full sm:w-auto px-10 py-5 bg-white/5 border border-white/10 rounded-full font-black text-lg hover:bg-white/10 transition-all duration-500 backdrop-blur-md flex items-center justify-center gap-2" data-id="element-2220">
                <Play size={20} fill="currentColor" data-id="element-2221" />
                See How It Works
              </button>
            </motion.div>
          </div>

          {/* Dashboard Mockup */}
          <motion.div initial={{
          opacity: 0,
          y: 40,
          rotateX: 15
        }} animate={{
          opacity: 1,
          y: 0,
          rotateX: 0
        }} transition={{
          duration: 1,
          delay: 0.4
        }} className="relative max-w-6xl mx-auto perspective-2000" data-id="element-2222">
            <div className="relative bg-[#1A1A1C] rounded-[32px] border border-white/10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] overflow-hidden p-4 md:p-8" data-id="element-2223">
              {/* Mockup Header */}
              <div className="flex items-center justify-between mb-8 border-b border-white/5 pb-6" data-id="element-2224">
                <div className="flex items-center gap-4" data-id="element-2225">
                  <div className="flex gap-1.5" data-id="element-2226">
                    <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/40" data-id="element-2227"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/40" data-id="element-2228"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/40" data-id="element-2229"></div>
                  </div>
                  <div className="h-4 w-32 bg-white/5 rounded-full" data-id="element-2230"></div>
                </div>
                <div className="flex items-center gap-4" data-id="element-2231">
                  <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10" data-id="element-2232"></div>
                  <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10" data-id="element-2233"></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2234">
                {[{
                label: 'Total Revenue',
                value: '$124,500',
                trend: '+12.4%',
                color: 'text-[#10B981]'
              }, {
                label: 'Food Cost %',
                value: '28.4%',
                trend: '-2.1%',
                color: 'text-[#10B981]'
              }, {
                label: 'Labor Cost %',
                value: '31.2%',
                trend: '+0.4%',
                color: 'text-red-400'
              }].map((stat, i) => <div key={i} className="p-6 bg-white/5 border border-white/10 rounded-2xl" data-id="element-2235">
                    <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2236">
                      {stat.label}
                    </p>
                    <div className="flex items-end justify-between" data-id="element-2237">
                      <p className="text-3xl font-black" data-id="element-2238">{stat.value}</p>
                      <span className={`text-xs font-bold ${stat.color}`} data-id="element-2239">
                        {stat.trend}
                      </span>
                    </div>
                  </div>)}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2240">
                <div className="h-64 bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col" data-id="element-2241">
                  <div className="flex items-center justify-between mb-6" data-id="element-2242">
                    <h4 className="text-sm font-bold text-white/60" data-id="element-2243">
                      Weekly Profit Margin
                    </h4>
                    <BarChart3 size={18} className="text-white/20" data-id="element-2244" />
                  </div>
                  <div className="flex-1 flex items-end gap-2" data-id="element-2245">
                    {[40, 70, 45, 90, 65, 80, 55, 95, 75, 85].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} animate={{
                    height: `${h}%`
                  }} transition={{
                    delay: 1 + i * 0.05
                  }} className={`flex-1 rounded-t-lg ${i === 7 ? 'bg-[#10B981]' : 'bg-white/10'}`} data-id="element-2246" />)}
                  </div>
                </div>
                <div className="h-64 bg-white/5 border border-white/10 rounded-2xl p-6" data-id="element-2247">
                  <div className="flex items-center justify-between mb-6" data-id="element-2248">
                    <h4 className="text-sm font-bold text-white/60" data-id="element-2249">
                      Expense Breakdown
                    </h4>
                    <PieChart size={18} className="text-white/20" data-id="element-2250" />
                  </div>
                  <div className="space-y-4" data-id="element-2251">
                    {[{
                    label: 'Food & Beverage',
                    value: '35%',
                    width: 'w-[35%]',
                    color: 'bg-[#10B981]'
                  }, {
                    label: 'Labor & Payroll',
                    value: '32%',
                    width: 'w-[32%]',
                    color: 'bg-emerald-600'
                  }, {
                    label: 'Rent & Utilities',
                    value: '15%',
                    width: 'w-[15%]',
                    color: 'bg-emerald-800'
                  }, {
                    label: 'Other',
                    value: '18%',
                    width: 'w-[18%]',
                    color: 'bg-white/10'
                  }].map((item, i) => <div key={i} data-id="element-2252">
                        <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-2253">
                          <span data-id="element-2254">{item.label}</span>
                          <span className="text-white/40" data-id="element-2255">{item.value}</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-2256">
                          <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: item.value
                      }} transition={{
                        duration: 1,
                        delay: 1.5
                      }} className={`h-full ${item.color}`} data-id="element-2257" />
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div animate={{
            y: [0, -20, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-12 -right-12 hidden lg:block p-6 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl z-20" data-id="element-2258">
              <div className="flex items-center gap-4 mb-4" data-id="element-2259">
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center text-red-400" data-id="element-2260">
                  <AlertCircle size={20} data-id="element-2261" />
                </div>
                <div data-id="element-2262">
                  <p className="text-xs font-bold text-white/40 uppercase" data-id="element-2263">
                    Inventory Alert
                  </p>
                  <p className="text-sm font-bold" data-id="element-2264">Wagyu Beef Low</p>
                </div>
              </div>
              <div className="h-1.5 w-32 bg-white/5 rounded-full overflow-hidden" data-id="element-2265">
                <div className="h-full w-1/4 bg-red-500" data-id="element-2266"></div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 20, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -bottom-12 -left-12 hidden lg:block p-6 bg-[#10B981]/10 backdrop-blur-2xl border border-[#10B981]/20 rounded-2xl shadow-2xl z-20" data-id="element-2267">
              <div className="flex items-center gap-4" data-id="element-2268">
                <div className="w-10 h-10 bg-[#10B981]/20 rounded-full flex items-center justify-center text-[#10B981]" data-id="element-2269">
                  <TrendingDown size={20} data-id="element-2270" />
                </div>
                <div data-id="element-2271">
                  <p className="text-xs font-bold text-white/40 uppercase" data-id="element-2272">
                    Cost Reduction
                  </p>
                  <p className="text-sm font-bold" data-id="element-2273">Waste down 14%</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Trust Row */}
          <div className="mt-32 pt-20 border-t border-white/5" data-id="element-2274">
            <p className="text-center text-white/40 font-bold uppercase tracking-[0.3em] text-xs mb-12" data-id="element-2275">
              Built for restaurant operators who are tired of:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 text-center" data-id="element-2276">
              {['Unknown Margins', 'Overspending', 'Financial Lag', 'Manual Inventory', 'Zero Visibility'].map((item, i) => <div key={i} className="text-white/60 font-medium hover:text-white transition-colors cursor-default" data-id="element-2277">
                  {item}
                </div>)}
            </div>
            <p className="text-center mt-16 text-xl font-medium text-white/80 italic" data-id="element-2278">
              "ROI gives operators a faster way to understand the health of
              their business."
            </p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-32 px-6 bg-[#0D0D0E] relative overflow-hidden" data-id="element-2279">
        <div className="max-w-7xl mx-auto" data-id="element-2280">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-2281">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2282">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-8" data-id="element-2283">
                Most Restaurant Owners Know Sales… <br data-id="element-2284" />
                <span className="text-red-500" data-id="element-2285">
                  But Don’t Know What They Actually Keep.
                </span>
              </h2>
              <p className="text-xl text-white/60 font-medium leading-relaxed mb-12" data-id="element-2286">
                You might make $20,000 this week… But: How much went to food
                costs? Labor? Utilities? Waste? Inventory loss? Overspending?
              </p>
              <div className="space-y-6" data-id="element-2287">
                {[{
                label: 'Food Cost Leaks',
                desc: 'Untracked price fluctuations from suppliers.'
              }, {
                label: 'Labor Inefficiency',
                desc: 'Scheduling without real-time demand data.'
              }, {
                label: 'Inventory Shrinkage',
                desc: 'Waste and theft that goes unnoticed for weeks.'
              }].map((item, i) => <div key={i} className="flex gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-red-500/30 transition-all group" data-id="element-2288">
                    <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center text-red-500 shrink-0 group-hover:scale-110 transition-transform" data-id="element-2289">
                      <TrendingDown size={24} data-id="element-2290" />
                    </div>
                    <div data-id="element-2291">
                      <h4 className="text-lg font-bold mb-1" data-id="element-2292">{item.label}</h4>
                      <p className="text-white/40 text-sm" data-id="element-2293">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
              <p className="mt-12 text-lg font-bold text-white/80" data-id="element-2294">
                Most operators never see the full picture until it’s already
                hurting the business.{' '}
                <span className="text-[#10B981]" data-id="element-2295">ROI changes that.</span>
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-2296">
              <div className="aspect-square bg-gradient-to-br from-red-500/20 to-transparent rounded-full blur-[100px] absolute inset-0" data-id="element-2297"></div>
              <div className="relative bg-[#1A1A1C] rounded-[40px] border border-white/10 p-12 shadow-2xl overflow-hidden" data-id="element-2298">
                <div className="text-center mb-12" data-id="element-2299">
                  <h3 className="text-2xl font-black mb-2" data-id="element-2300">
                    The Financial Leak
                  </h3>
                  <p className="text-white/40 text-sm font-medium" data-id="element-2301">
                    Where your profit disappears
                  </p>
                </div>

                <div className="relative h-64 flex items-center justify-center" data-id="element-2302">
                  {/* Animated Leak Visual */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-2303">
                    <motion.div animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity
                  }} className="w-48 h-48 bg-red-500/20 rounded-full blur-3xl" data-id="element-2304"></motion.div>
                  </div>
                  <div className="relative z-10 text-center" data-id="element-2305">
                    <motion.p animate={{
                    y: [0, 10, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity
                  }} className="text-6xl font-black text-red-500 mb-2" data-id="element-2306">
                      -$4,280
                    </motion.p>
                    <p className="text-sm font-bold text-white/40 uppercase tracking-widest" data-id="element-2307">
                      Weekly Leakage
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-8" data-id="element-2308">
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-2309">
                    <p className="text-[10px] font-bold text-white/40 uppercase mb-1" data-id="element-2310">
                      Waste
                    </p>
                    <p className="text-lg font-bold text-red-400" data-id="element-2311">-$1,240</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-2312">
                    <p className="text-[10px] font-bold text-white/40 uppercase mb-1" data-id="element-2313">
                      Over-staffing
                    </p>
                    <p className="text-lg font-bold text-red-400" data-id="element-2314">-$2,040</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-32 px-6 relative" data-id="element-2315">
        <div className="max-w-7xl mx-auto" data-id="element-2316">
          <div className="text-center max-w-3xl mx-auto mb-24" data-id="element-2317">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2318">
              Operational Clarity — <br data-id="element-2319" />
              <span className="text-[#10B981]" data-id="element-2320">Without the Complexity</span>
            </h2>
            <p className="text-xl text-white/60 font-medium" data-id="element-2321">
              All designed around how restaurants actually operate. Not generic
              accounting software.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-2322">
            {[{
            title: 'Track Money In/Out',
            icon: Wallet,
            desc: 'Real-time cash flow monitoring across all locations.'
          }, {
            title: 'Monitor Food & Labor',
            icon: ChefHat,
            desc: 'Dynamic cost analysis that updates as prices change.'
          }, {
            title: 'Inventory Visibility',
            icon: Layers,
            desc: 'Predictive stock management to eliminate waste.'
          }, {
            title: 'Faster Decisions',
            icon: Zap,
            desc: 'Actionable insights delivered when they matter most.'
          }, {
            title: 'Identify Profit Leaks',
            icon: AlertCircle,
            desc: 'AI-driven detection of operational inefficiencies.'
          }, {
            title: 'Operational Spending',
            icon: DollarSign,
            desc: 'Complete transparency into every dollar spent.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} whileHover={{
            y: -10
          }} className="p-8 bg-white/5 border border-white/10 rounded-[32px] hover:border-[#10B981]/30 transition-all group" data-id="element-2323">
                <div className="w-14 h-14 bg-[#10B981]/10 rounded-2xl flex items-center justify-center text-[#10B981] mb-8 group-hover:bg-[#10B981] group-hover:text-white transition-all duration-500" data-id="element-2324">
                  <item.icon size={28} data-id="element-2325" />
                </div>
                <h3 className="text-2xl font-black mb-4" data-id="element-2326">{item.title}</h3>
                <p className="text-white/40 font-medium leading-relaxed" data-id="element-2327">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>

          <div className="relative bg-gradient-to-br from-[#10B981]/20 to-transparent rounded-[40px] p-12 border border-[#10B981]/20 overflow-hidden" data-id="element-2328">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10" data-id="element-2329">
              <div data-id="element-2330">
                <h3 className="text-3xl font-black mb-6" data-id="element-2331">
                  Seamless Integration
                </h3>
                <p className="text-white/60 text-lg mb-8 leading-relaxed" data-id="element-2332">
                  ROI connects directly to your POS, supplier portals, and
                  payroll systems to create a unified intelligence layer.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-2333">
                  {['Toast', 'Square', 'Clover', 'Lightspeed', 'ADP', 'Sysco'].map(brand => <div key={brand} className="px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white/40" data-id="element-2334">
                      {brand}
                    </div>)}
                </div>
              </div>
              <div className="relative h-64 flex items-center justify-center" data-id="element-2335">
                {/* Animated Connecting Lines Visual */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-2336">
                  <div className="w-24 h-24 bg-[#10B981] rounded-2xl flex items-center justify-center shadow-[0_0_50px_rgba(16,185,129,0.5)] z-10" data-id="element-2337">
                    <TrendingUp size={40} data-id="element-2338" />
                  </div>
                  {[0, 60, 120, 180, 240, 300].map(deg => <motion.div key={deg} animate={{
                  opacity: [0.2, 1, 0.2]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: deg / 360
                }} className="absolute w-40 h-[1px] bg-gradient-to-r from-transparent via-[#10B981] to-transparent" style={{
                  transform: `rotate(${deg}deg)`
                }} data-id="element-2339"></motion.div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiator Section */}
      <section className="py-32 px-6 bg-[#0D0D0E]" data-id="element-2340">
        <div className="max-w-7xl mx-auto" data-id="element-2341">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-2342">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="lg:order-2" data-id="element-2343">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2344">
                Built for <span className="text-[#10B981]" data-id="element-2345">Speed</span>
              </h2>
              <p className="text-xl text-white/60 font-medium leading-relaxed mb-12" data-id="element-2346">
                Most systems take months to implement. ROI is designed to help
                operators start seeing insights quickly — so you can make
                informed decisions sooner, not later.
              </p>

              <div className="space-y-8" data-id="element-2347">
                <div className="flex items-center gap-6" data-id="element-2348">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-white/40 font-black" data-id="element-2349">
                    01
                  </div>
                  <div data-id="element-2350">
                    <h4 className="text-lg font-bold" data-id="element-2351">Instant Data Sync</h4>
                    <p className="text-white/40 text-sm" data-id="element-2352">
                      Connect your POS in under 5 minutes.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6" data-id="element-2353">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-white/40 font-black" data-id="element-2354">
                    02
                  </div>
                  <div data-id="element-2355">
                    <h4 className="text-lg font-bold" data-id="element-2356">Automated Mapping</h4>
                    <p className="text-white/40 text-sm" data-id="element-2357">
                      Our AI categorizes your expenses automatically.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6" data-id="element-2358">
                  <div className="w-12 h-12 bg-[#10B981]/20 rounded-full flex items-center justify-center text-[#10B981] font-black" data-id="element-2359">
                    03
                  </div>
                  <div data-id="element-2360">
                    <h4 className="text-lg font-bold" data-id="element-2361">Live in 48 Hours</h4>
                    <p className="text-white/40 text-sm" data-id="element-2362">
                      Start seeing real-time profit visibility by day two.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="lg:order-1" data-id="element-2363">
              <div className="grid grid-cols-2 gap-4" data-id="element-2364">
                {[{
                label: 'One Restaurant',
                icon: Utensils
              }, {
                label: 'Café',
                icon: ChefHat
              }, {
                label: 'Deli',
                icon: Layers
              }, {
                label: 'Multiple Locations',
                icon: Globe
              }].map((item, i) => <motion.div key={i} whileHover={{
                scale: 1.05
              }} className="p-8 bg-white/5 border border-white/10 rounded-3xl text-center flex flex-col items-center gap-4" data-id="element-2365">
                    <item.icon size={32} className="text-[#10B981]" data-id="element-2366" />
                    <p className="font-bold text-sm" data-id="element-2367">{item.label}</p>
                  </motion.div>)}
              </div>
              <p className="text-center mt-8 text-white/40 font-bold uppercase tracking-widest text-xs" data-id="element-2368">
                ROI adapts to your workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="benefits" className="py-32 px-6" data-id="element-2369">
        <div className="max-w-7xl mx-auto" data-id="element-2370">
          <div className="text-center max-w-3xl mx-auto mb-24" data-id="element-2371">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2372">
              What Restaurant <br data-id="element-2373" />
              <span className="text-[#10B981]" data-id="element-2374">Owners Gain</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2375">
            {[{
            title: 'Better Visibility',
            desc: 'Understand where your money is actually going.',
            metric: '100%',
            metricLabel: 'Transparency',
            icon: Eye
          }, {
            title: 'Faster Decisions',
            desc: 'Know what needs attention before it becomes a bigger problem.',
            metric: '4x',
            metricLabel: 'Faster Action',
            icon: Zap
          }, {
            title: 'Operational Control',
            desc: 'Track spending habits, inventory behavior, and business performance.',
            metric: '22%',
            metricLabel: 'Cost Savings',
            icon: LayoutDashboard
          }, {
            title: 'Scalability',
            desc: 'Create systems that grow with your restaurant.',
            metric: '∞',
            metricLabel: 'Growth Potential',
            icon: TrendingUp
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative bg-[#1A1A1C] rounded-[40px] border border-white/10 p-10 overflow-hidden" data-id="element-2376">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#10B981]/5 blur-[80px] rounded-full -mr-32 -mt-32 group-hover:bg-[#10B981]/10 transition-colors duration-700" data-id="element-2377"></div>

                <div className="relative z-10 flex flex-col h-full" data-id="element-2378">
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-[#10B981] mb-8" data-id="element-2379">
                    <item.icon size={32} data-id="element-2380" />
                  </div>
                  <h3 className="text-3xl font-black mb-4" data-id="element-2381">{item.title}</h3>
                  <p className="text-white/60 text-lg mb-12 leading-relaxed" data-id="element-2382">
                    {item.desc}
                  </p>

                  <div className="mt-auto pt-8 border-t border-white/5 flex items-end justify-between" data-id="element-2383">
                    <div data-id="element-2384">
                      <p className="text-5xl font-black text-white mb-1" data-id="element-2385">
                        {item.metric}
                      </p>
                      <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2386">
                        {item.metricLabel}
                      </p>
                    </div>
                    <div className="w-24 h-12 bg-white/5 rounded-xl border border-white/10 flex items-center justify-center" data-id="element-2387">
                      <LineChart size={24} className="text-[#10B981]" data-id="element-2388" />
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Soft Authority Section */}
      <section id="about" className="py-32 px-6 bg-[#0D0D0E]" data-id="element-2389">
        <div className="max-w-7xl mx-auto" data-id="element-2390">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-2391">
            <div className="relative rounded-[40px] overflow-hidden aspect-[4/5] group" data-id="element-2392">
              <img src="https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&q=80&w=1200" alt="Restaurant Operations" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s]" data-id="element-2393" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" data-id="element-2394"></div>
              <div className="absolute bottom-12 left-12 right-12" data-id="element-2395">
                <div className="p-8 bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl" data-id="element-2396">
                  <p className="text-lg font-medium italic text-white/90 mb-4" data-id="element-2397">
                    "We built ROI because we were tired of generic tools that
                    didn't understand how a kitchen actually runs."
                  </p>
                  <div className="flex items-center gap-4" data-id="element-2398">
                    <div className="w-12 h-12 rounded-full bg-[#10B981]" data-id="element-2399"></div>
                    <div data-id="element-2400">
                      <p className="font-bold" data-id="element-2401">Marcus Chen</p>
                      <p className="text-xs text-white/40 font-bold uppercase" data-id="element-2402">
                        Founder & Former Operator
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-2403">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8" data-id="element-2404">
                Built Around Real <br data-id="element-2405" />
                <span className="text-[#10B981]" data-id="element-2406">Restaurant Operations</span>
              </h2>
              <div className="space-y-8 text-xl text-white/60 font-medium leading-relaxed" data-id="element-2407">
                <p data-id="element-2408">
                  ROI was created from years of hands-on restaurant operational
                  experience — built specifically around the day-to-day
                  realities restaurant owners face.
                </p>
                <p data-id="element-2409">
                  This isn’t generic software built by people outside the
                  industry. It’s designed for operators.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-8 mt-12" data-id="element-2410">
                <div className="space-y-2" data-id="element-2411">
                  <h4 className="text-3xl font-black text-white" data-id="element-2412">15+</h4>
                  <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2413">
                    Years in Operations
                  </p>
                </div>
                <div className="space-y-2" data-id="element-2414">
                  <h4 className="text-3xl font-black text-white" data-id="element-2415">500+</h4>
                  <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2416">
                    Kitchens Optimized
                  </p>
                </div>
              </div>

              <button className="mt-12 px-10 py-5 border border-white/10 rounded-full font-black text-lg hover:bg-white hover:text-black transition-all duration-500" data-id="element-2417">
                Read Our Story
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-40 px-6 relative overflow-hidden" data-id="element-2418">
        <div className="absolute inset-0 bg-[#10B981]/5" data-id="element-2419"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none" data-id="element-2420">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-[#10B981]/10 blur-[150px] rounded-full" data-id="element-2421"></div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10 text-center" data-id="element-2422">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2423">
            <h2 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 leading-[0.9]" data-id="element-2424">
              Ready to See What Your <br data-id="element-2425" />
              <span className="text-[#10B981]" data-id="element-2426">Numbers Are Really Saying?</span>
            </h2>
            <p className="text-xl md:text-2xl text-white/60 font-medium mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-2427">
              Book a quick walkthrough and see how ROI can help you better
              understand your restaurant operations.
            </p>
            <button className="px-12 py-6 bg-[#10B981] text-white rounded-full font-black text-xl hover:bg-[#059669] transition-all duration-500 shadow-[0_20px_50px_rgba(16,185,129,0.3)] group flex items-center justify-center gap-3 mx-auto" data-id="element-2428">
              Book Your Demo
              <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" data-id="element-2429" />
            </button>
            <p className="mt-8 text-white/40 font-bold uppercase tracking-widest text-xs" data-id="element-2430">
              No credit card required • 15-minute walkthrough
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 px-6 border-t border-white/5 bg-[#0A0A0B]" data-id="element-2431">
        <div className="max-w-7xl mx-auto" data-id="element-2432">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-20" data-id="element-2433">
            <div className="col-span-2 md:col-span-1" data-id="element-2434">
              <div className="flex items-center gap-2 mb-8" data-id="element-2435">
                <div className="w-8 h-8 bg-[#10B981] rounded-lg flex items-center justify-center" data-id="element-2436">
                  <TrendingUp size={20} data-id="element-2437" />
                </div>
                <span className="text-xl font-black tracking-tighter" data-id="element-2438">ROI</span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed max-w-xs" data-id="element-2439">
                The premium intelligence platform for serious restaurant
                operators. Built for speed, clarity, and control.
              </p>
            </div>
            <div data-id="element-2440">
              <h4 className="text-xs font-black uppercase tracking-widest text-white mb-6" data-id="element-2441">
                Product
              </h4>
              <ul className="space-y-4 text-sm font-medium text-white/40" data-id="element-2442">
                <li data-id="element-2443">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2444">
                    Features
                  </a>
                </li>
                <li data-id="element-2445">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2446">
                    Integrations
                  </a>
                </li>
                <li data-id="element-2447">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2448">
                    Pricing
                  </a>
                </li>
                <li data-id="element-2449">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2450">
                    Changelog
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-2451">
              <h4 className="text-xs font-black uppercase tracking-widest text-white mb-6" data-id="element-2452">
                Company
              </h4>
              <ul className="space-y-4 text-sm font-medium text-white/40" data-id="element-2453">
                <li data-id="element-2454">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2455">
                    About Us
                  </a>
                </li>
                <li data-id="element-2456">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2457">
                    Careers
                  </a>
                </li>
                <li data-id="element-2458">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2459">
                    Privacy
                  </a>
                </li>
                <li data-id="element-2460">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2461">
                    Terms
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-2462">
              <h4 className="text-xs font-black uppercase tracking-widest text-white mb-6" data-id="element-2463">
                Connect
              </h4>
              <ul className="space-y-4 text-sm font-medium text-white/40" data-id="element-2464">
                <li data-id="element-2465">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2466">
                    Twitter
                  </a>
                </li>
                <li data-id="element-2467">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2468">
                    LinkedIn
                  </a>
                </li>
                <li data-id="element-2469">
                  <a href="#" className="hover:text-[#10B981] transition-colors" data-id="element-2470">
                    Instagram
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-2471">
            <p className="text-xs font-bold text-white/20 uppercase tracking-widest" data-id="element-2472">
              © 2026 ROI Intelligence Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-white/20 text-xs font-bold uppercase tracking-widest" data-id="element-2473">
              <span data-id="element-2474">Built for Operators</span>
              <span className="w-1 h-1 bg-white/10 rounded-full" data-id="element-2475"></span>
              <span data-id="element-2476">Enterprise Ready</span>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}